// App.tsx — Doclenz Admin
import 'reflect-metadata'
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/dropzone/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/spotlight/styles.css'
import '@archbase/components/dist/index.css'
import '@archbase/layout/dist/index.css'
import '@archbase/admin/dist/index.css'

import {
  ActionIcon,
  Badge,
  Flex,
  Group,
  Image,
  MantineThemeOverride,
  Menu,
  Stack,
  Text,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { useFullscreenDocument, useHotkeys, useLocalStorage, useMediaQuery } from '@mantine/hooks'
import { ErrorInfo, Fragment, ReactNode, useEffect, useState } from 'react'
import {
  IconArrowsMaximize,
  IconLogout,
  IconMoonStars,
  IconSun,
  IconUserShield,
} from '@tabler/icons-react'

import { Login } from '@views/login/Login'
import translation_en from '@locales/en/translation.json'
import translation_ptbr from '@locales/pt-BR/translation.json'
import translation_es from '@locales/es/translation.json'
import containerIOC from '@ioc/ContainerIOC'
import { AppThemeDark, AppThemeLight, DoclenzTokens } from '@theme/index'
import { navigationData } from './navigation/navigationData'
import { ErrorFallback } from '@utils/ErrorFallback'
import { logo, horizontalFor } from '@assets/logo'
import { AppUser, AppAuthenticator, currentAdminKey } from '@auth/AppAuthenticator'
import { clearCurrentUser, loadCurrentUser, persistCurrentUser } from '@auth/currentUser'
import { APP_NAME, APP_VERSION, STORAGE_KEYS, TRANSLATION_NAME } from './AppConstants'

import {
  ARCHBASE_IOC_API_TYPE,
  ArchbaseAppProvider,
  ArchbaseErrorBoundary,
  ArchbaseGlobalProvider,
  archbaseI18next,
  useArchbaseTheme,
} from '@archbase/core'
import { useArchbaseStore } from '@archbase/data'
import { ArchbaseSecurityProvider } from '@archbase/security'
import {
  ArchbaseAdminMainLayout,
  ArchbaseAdminTabContainer,
  ArchbaseNavigationProvider,
  ArchbaseTabItem,
  CommandPaletteButton,
  useArchbaseAdminStore,
} from '@archbase/admin'

type MainProps = {
  user?: AppUser
  onLogin: (adminKey: string) => Promise<void>
  onLogout: () => void
  error: string | null
}

function Main({ user, onLogin, onLogout, error }: MainProps) {
  const { toggle } = useFullscreenDocument()
  const theme = useArchbaseTheme()
  const isMedium = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const adminStore = useArchbaseAdminStore()
  const templateStore = useArchbaseStore()
  const [isCollapsed, setCollapsed] = useState(false)
  const [sidebarVariant, setSidebarVariant] = useLocalStorage<'standard' | 'rail' | 'minimal'>({
    key: 'doclenz-sidebar-variant',
    defaultValue: 'rail',
  })
  const isCompact = isCollapsed || sidebarVariant === 'minimal'
  // Badge DEV quando o console fala com uma API local — evita confundir ambiente ao operar.
  const apiTarget = import.meta.env.VITE_API || window.location.origin
  const isDev = apiTarget.includes('localhost') || apiTarget.includes('127.0.0.1')

  const handleLogout = () => {
    templateStore.reset()
    onLogout()
  }

  const headerActions = (): ReactNode[] => [
    <Tooltip key="fullscreen" withinPortal withArrow label="Tela cheia">
      <ActionIcon variant="transparent" c="#868E96" onClick={toggle}>
        <IconArrowsMaximize size="1.5rem" />
      </ActionIcon>
    </Tooltip>,
  ]

  const logError = (err: Error, info: ErrorInfo) => console.error(err, info)

  if (!user) {
    return <Login onLogin={onLogin} error={error} />
  }

  return (
    <ArchbaseErrorBoundary FallbackComponent={ErrorFallback} onError={logError}>
      <ArchbaseAdminMainLayout
        sideBarCollapsedSubmenuWidth={300}
        sideBarTextDarkColor="white"
        sideBarTextLightColor="white"
        // Ícones em azul acinzentado, não na primária: o turquesa fica neon sobre o azul-marinho
        // e competiria com o item ativo. O rótulo segue branco, então texto > ícone se mantém.
        sideBarIconDarkColor={DoclenzTokens.sidebarIcon}
        sideBarIconLightColor={DoclenzTokens.sidebarIcon}
        // A barra lateral é azul-marinho nos dois esquemas — é a pele da marca, não do tema.
        sideBarBackgroundDarkColor={DoclenzTokens.navy950}
        sideBarBackgroundLightColor={DoclenzTokens.navy950}
        sideBarBackgroundImage={DoclenzTokens.sidebarGradient}
        // Mesmo raio do `defaultRadius: 'lg'` (16px) do tema: sem isso o container
        // fica com os 4px padrão do archbase e o conteúdo, mais arredondado, deixa
        // uma cunha nos cantos de baixo.
        mainBorderRadius={16}
        mainBorderColor={
          colorScheme === 'dark' ? DoclenzTokens.dark.border : DoclenzTokens.light.border
        }
        sideBarItemBorderRadius={11}
        sideBarItemHorizontalGap={10}
        sideBarItemActiveBackground={DoclenzTokens.sidebarItemActive}
        // Valor único, não par claro/escuro — quem escolhe entre as variantes é o colorScheme.
        mainBackground={
          colorScheme === 'dark' ? DoclenzTokens.mainBackgroundDark : DoclenzTokens.mainBackground
        }
        showHeader={isMedium}
        onCollapsedSideBar={setCollapsed}
        navigationData={navigationData}
        sideBarWidth="280px"
        sideBarCollapsedWidth="60px"
        navigationRootLink="/"
        menuItemHeight={isCollapsed ? 44 : 34}
        sideBarFooterHeight={isMedium ? 96 : 110}
        sideBarHeaderHeight={isMedium ? 0 : 54}
        // Não há modelo de permissões: quem tem a chave de admin pode tudo.
        enableSecurity={false}
        sidebarVariant={sidebarVariant}
        showCollapsedButton={sidebarVariant !== 'minimal'}
        sideBarHeaderContent={
          !isMedium && (
            <Group
              py={2}
              pr={isCollapsed ? 0 : 16}
              bg="rgba(0,0,0,.18)"
              wrap="nowrap"
              gap={8}
              justify={isCollapsed ? 'center' : 'space-between'}
            >
              {/* A barra lateral e azul escura mesmo no tema claro, entao aqui vale sempre a
                  variante de fundo escuro — a clara tem o texto em azul-marinho e sumiria.

                  42 px de altura, nao 30: o PNG tem margem transparente em volta, entao a
                  marca desenhada ocupa bem menos que a caixa. Em 30 o wordmark ficava com
                  84 px numa barra de 280 e mal se lia. */}
              <Image
                src={isCollapsed ? logo.mark : horizontalFor('dark')}
                alt="Doclenz"
                h={isCollapsed ? 34 : 42}
                w="auto"
                fit="contain"
                ml={isCollapsed ? 0 : 10}
              />
              {!isCollapsed && <CommandPaletteButton navigationData={navigationData} />}
            </Group>
          )
        }
        sideBarFooterContent={
          <Stack gap={0}>
            {/* Card do administrador: borda turquesa translúcida sobre fundo escurecido, para
                destacar do gradiente da barra sem virar outro bloco chapado. Mesmo tratamento
                do `.workspace` no vendax-promoter-admin, com o acento do Doclenz. */}
            <Flex
              mx={isCompact ? 6 : 12}
              mb={8}
              px={isCompact ? 6 : 12}
              py={10}
              align="center"
              justify={isCompact ? 'center' : 'space-between'}
              style={{
                border: '1px solid rgba(30,196,212,.22)',
                backgroundColor: 'rgba(0,0,0,.11)',
                borderRadius: 13,
              }}
            >
              <Menu shadow="md" width={220} position="top-end" withArrow arrowPosition="center" offset={5}>
                <Menu.Target>
                  <Group
                    gap={12}
                    style={{ cursor: 'pointer' }}
                    justify={isCompact ? 'center' : 'flex-start'}
                  >
                    {/* Escudo, não porta de saída: este é o alvo do menu, e sair é só um dos
                        itens dentro dele. O ícone de logout aqui sugeria que clicar desconecta. */}
                    <IconUserShield size={22} color="white" />
                    {!isCompact && (
                      <Stack gap={0}>
                        <Text c="white" size="sm" fw={500}>
                          Administrador
                        </Text>
                        {isDev && (
                          <Badge size="xs" variant="light" color="appAccent">
                            DEV
                          </Badge>
                        )}
                      </Stack>
                    )}
                  </Group>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Opções</Menu.Label>
                  <Menu.Item
                    leftSection={
                      colorScheme === 'dark' ? <IconSun size={14} /> : <IconMoonStars size={14} />
                    }
                    onClick={() => toggleColorScheme()}
                  >
                    {archbaseI18next.t('archbase:toggleColorScheme')}
                  </Menu.Item>
                  <Menu.Item leftSection={<IconArrowsMaximize size={14} />} onClick={toggle}>
                    Tela cheia
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Label>Layout do menu</Menu.Label>
                  <Menu.Item onClick={() => setSidebarVariant('standard')}>Padrão</Menu.Item>
                  <Menu.Item onClick={() => setSidebarVariant('rail')}>Rail</Menu.Item>
                  <Menu.Item onClick={() => setSidebarVariant('minimal')}>Minimal</Menu.Item>
                  <Menu.Divider />
                  <Menu.Item color="red" leftSection={<IconLogout size={14} />} onClick={handleLogout}>
                    Sair
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Flex>
            {/* Faixa da versão: translúcida sobre o gradiente, não um bloco azul chapado. */}
            <Flex
              justify={isCompact ? 'center' : 'space-between'}
              align="center"
              px={isCompact ? 4 : 12}
              style={{
                backgroundColor: 'rgba(0,0,0,.14)',
                borderTop: '1px solid rgba(30,196,212,.14)',
              }}
              h={44}
            >
              {!isCompact && (
                <Text size="sm" c="rgba(255,255,255,.75)" fw={500}>
                  {APP_NAME}
                </Text>
              )}
              <Badge size="sm" variant="light" color="appAccent">
                {isCompact ? APP_VERSION : `v${APP_VERSION}`}
              </Badge>
            </Flex>
          </Stack>
        }
        header={<Fragment>{headerActions()}</Fragment>}
      >
        <ArchbaseAdminTabContainer
          onChangeActiveTabId={(id) => adminStore.setActiveTabId(id)}
          onChangeOpenedTabs={(tabs: ArchbaseTabItem[]) => adminStore.setOpenedTabs(tabs)}
          openedTabs={adminStore.openedTabs}
          activeTabId={adminStore.activeTabId}
          navigationData={navigationData}
          colors={colorScheme === 'dark' ? DoclenzTokens.tabsDark : DoclenzTokens.tabs}
        />
      </ArchbaseAdminMainLayout>
    </ArchbaseErrorBoundary>
  )
}

/**
 * Não usa o `useArchbaseAuthenticationManager` de propósito.
 *
 * Aquele gerenciador pressupõe login por usuário e senha, com access token e refresh — nada
 * disso existe no doclenz-api, que confere um `X-Admin-Key` estático. Aqui o estado de sessão é
 * apenas "há chave válida guardada ou não", e quem valida é o `AppAuthenticator`.
 */
function App() {
  const [colorScheme, setColorScheme] = useLocalStorage<'light' | 'dark'>({
    key: 'mantine-color-scheme',
    defaultValue: 'light',
    getInitialValueInEffect: true,
  })
  const [dark, setThemeDark] = useState<MantineThemeOverride>(AppThemeDark)
  const [light, setThemeLight] = useState<MantineThemeOverride>(AppThemeLight)
  const [user, setUser] = useState<AppUser | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  // A sessão sobrevive ao refresh da página: se a chave ainda está guardada, segue dentro.
  useEffect(() => {
    if (currentAdminKey()) {
      setUser(loadCurrentUser() ?? new AppUser({}))
    }
  }, [])

  const toggleColorScheme = (value?: 'light' | 'dark') =>
    setColorScheme(value || (colorScheme === 'dark' ? 'light' : 'dark'))
  useHotkeys([['mod+J', () => toggleColorScheme()]])

  const handleLogin = async (adminKey: string) => {
    setError(null)
    try {
      const authenticator = containerIOC.get<AppAuthenticator>(ARCHBASE_IOC_API_TYPE.Authenticator)
      await authenticator.login('admin', adminKey)
      const loggedUser = new AppUser({})
      persistCurrentUser(loggedUser)
      setUser(loggedUser)
    } catch (err: any) {
      setError(err?.message || 'Não foi possível entrar.')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.ADMIN_KEY)
    clearCurrentUser()
    setUser(undefined)
  }

  return (
    <ArchbaseGlobalProvider
      colorScheme={colorScheme}
      containerIOC={containerIOC}
      themeDark={dark}
      themeLight={light}
      translationName={TRANSLATION_NAME}
      translationResource={{
        en: translation_en,
        'pt-BR': translation_ptbr,
        es: translation_es,
      }}
    >
      <ArchbaseSecurityProvider user={null}>
        <ArchbaseAppProvider
          user={user}
          owner={null}
          selectedCompany={undefined}
          variant="filled"
          setCustomTheme={(d: MantineThemeOverride, l: MantineThemeOverride) => {
            setThemeDark(d)
            setThemeLight(l)
          }}
          navigationProvider={ArchbaseNavigationProvider}
        >
          <ModalsProvider>
            <Notifications position="top-right" />
            <Main user={user} onLogin={handleLogin} onLogout={handleLogout} error={error} />
          </ModalsProvider>
        </ArchbaseAppProvider>
      </ArchbaseSecurityProvider>
    </ArchbaseGlobalProvider>
  )
}

export default App
